
import React, { useState } from 'react';
import Layout from '@/components/Layout/Layout';
import VariableList from '@/components/Variables/VariableList';
import VariableForm from '@/components/Variables/VariableForm';
import DatabaseVariablesList from '@/components/Variables/DatabaseVariablesList';
import { Variable, VariableCategory } from '@/types/variables';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Database } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

// Default variables to seed the database
const defaultVariables = [
  {
    name: 'businessName',
    description: 'The official name of your business',
    value: 'Carolina Trophy Roofs',
    category: 'Business Core' as VariableCategory,
    syntax: '{businessName}'
  },
  {
    name: 'businessPhone',
    description: 'Main business phone number',
    value: '(252) 302-2003',
    category: 'Contact Information' as VariableCategory,
    syntax: '{businessPhone}'
  },
  {
    name: 'businessEmail',
    description: 'Main business email address',
    value: 'Claims@CarolinaTrophyRoofs.com',
    category: 'Contact Information' as VariableCategory,
    syntax: '{businessEmail}'
  },
  {
    name: 'businessWebsite',
    description: 'Official business website',
    value: 'CarolinaTrophyRoofs.com',
    category: 'Contact Information' as VariableCategory,
    syntax: '{businessWebsite}'
  },
  {
    name: 'businessAddress',
    description: 'Physical business address',
    value: '104 S. Business Ct, Rocky Mount, NC 27804',
    category: 'Contact Information' as VariableCategory,
    syntax: '{businessAddress}'
  },
  {
    name: 'serviceArea',
    description: 'Primary service coverage area',
    value: 'Eastern North Carolina',
    category: 'Services & Products' as VariableCategory,
    syntax: '{serviceArea}'
  }
];

const Variables = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentVariable, setCurrentVariable] = useState<Variable | null>(null);
  const queryClient = useQueryClient();

  // Fetch variables from Supabase
  const { data: variables = [], isLoading } = useQuery({
    queryKey: ['contentVariables'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_variables')
        .select('*')
        .order('category', { ascending: true });
      
      if (error) throw error;
      
      // Transform to match Variable interface
      return data.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description || '',
        value: item.value,
        category: item.category as VariableCategory,
        syntax: item.syntax
      }));
    },
  });

  // Seed default variables mutation
  const seedVariablesMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .from('content_variables')
        .insert(defaultVariables);
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentVariables'] });
      toast.success('Default variables added successfully!');
    },
    onError: () => {
      toast.error('Failed to add default variables');
    }
  });

  // Create variable mutation
  const createVariableMutation = useMutation({
    mutationFn: async (newVariable: Omit<Variable, 'id'>) => {
      const { data, error } = await supabase
        .from('content_variables')
        .insert({
          name: newVariable.name,
          description: newVariable.description,
          value: newVariable.value,
          category: newVariable.category,
          syntax: newVariable.syntax
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentVariables'] });
      toast.success('New variable created successfully');
    },
    onError: () => {
      toast.error('Failed to create variable');
    }
  });

  // Update variable mutation
  const updateVariableMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string, updates: Partial<Variable> }) => {
      const { data, error } = await supabase
        .from('content_variables')
        .update({
          name: updates.name,
          description: updates.description,
          value: updates.value,
          category: updates.category,
          syntax: updates.syntax,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentVariables'] });
      toast.success('Variable updated successfully');
    },
    onError: () => {
      toast.error('Failed to update variable');
    }
  });

  // Delete variable mutation
  const deleteVariableMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('content_variables')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentVariables'] });
      toast.success('Variable deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete variable');
    }
  });

  const handleAddNew = () => {
    setCurrentVariable(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (variable: Variable) => {
    setCurrentVariable(variable);
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    deleteVariableMutation.mutate(id);
  };

  const handleSubmit = (values: { name: string; description: string; value: string; category: VariableCategory }) => {
    const variableData = {
      ...values,
      syntax: `{${values.name}}`
    };

    if (currentVariable) {
      // Update existing
      updateVariableMutation.mutate({
        id: currentVariable.id,
        updates: variableData
      });
    } else {
      // Create new
      createVariableMutation.mutate(variableData);
    }
    setIsDialogOpen(false);
  };

  const handleSeedVariables = () => {
    seedVariablesMutation.mutate();
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading variables...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Variable Manager</h1>
            <p className="text-muted-foreground mt-1">
              Create and manage content variables for your blog templates
            </p>
          </div>
          <Button onClick={handleAddNew} className="mt-4 md:mt-0">
            Add New Variable
          </Button>
        </div>

        {variables.length === 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                No Variables Found
              </CardTitle>
              <CardDescription>
                Get started by adding some default business variables to your database.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleSeedVariables} disabled={seedVariablesMutation.isPending}>
                {seedVariablesMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding Variables...
                  </>
                ) : (
                  'Add Default Variables'
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="manage" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manage">Manage Variables</TabsTrigger>
            <TabsTrigger value="database">Database Variables</TabsTrigger>
          </TabsList>
          
          <TabsContent value="manage" className="mt-6">
            <VariableList
              variables={variables}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAddNew={handleAddNew}
            />
          </TabsContent>
          
          <TabsContent value="database" className="mt-6">
            <DatabaseVariablesList />
          </TabsContent>
        </Tabs>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {currentVariable ? 'Edit Variable' : 'Add New Variable'}
              </DialogTitle>
              <DialogDescription>
                {currentVariable
                  ? 'Update the details for this variable.'
                  : 'Create a new variable for use in your templates.'}
              </DialogDescription>
            </DialogHeader>
            <VariableForm
              onSubmit={handleSubmit}
              initialValues={
                currentVariable
                  ? {
                      name: currentVariable.name,
                      description: currentVariable.description,
                      value: currentVariable.value,
                      category: currentVariable.category,
                    }
                  : undefined
              }
            />
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Variables;
